import React, {
  useEffect,
  useState,
  useMemo,
  type FunctionComponent,
} from "react";
import { ImageProps } from "./types";
import { sizeStyles } from "shared/src/styles/sizeStyles";
import { useImageResolver } from "shared/src/hooks/useImageResolver";
import { useCuiConfig } from "shared/src/hooks/useCuiConfig";
import { cssColor } from "shared/src/utils/cssUtils";
import { animationStyles } from "shared/src/styles/animationStyles";
import { customIcons, type CustomIconProps } from "./custom";
import { tailwindSemanticColorsAsBackground as twColors } from "shared/src/styles/colorStyles";

const exceptionsList = ["icon_ui_dots_vertical"];

// const brandIconsWithModesMap = brandIconsWithModes.reduce((acc, icon) => {
//   acc[icon] = { light: `${icon}_onLight`, dark: `${icon}_onDark` };
//   return acc;
// }, {});
const sizeUnitRE = /px|rem|em|pt/;
// const flagNameIgnoreWordsList = ["of", "the", "and"];

// Gotta Fix Sizes
// Gotta fix aspect ratios

export const Image = ({
  id = "",
  name,
  size = "md",
  className = "",
  animate = null,
  accessibilityLabel = "",
  options,
  mode = "light",
  defer,
  lazy = false,
  color = "fg-default",
  paddingClasses = "cui-p-0",
  marginClasses = "cui-m-0",
  style,
  secondaryColor,
  elementTiming,
  flipIcon,
  ...rest
}: ImageProps) => {
  const [loaded, setLoaded] = useState(true);

  useEffect(() => {
    setLoaded(true);
  }, [name]);

  /**
   * Resolve Icon
   */
  const config = useCuiConfig();
  const { src, srcSet, folder, canColor, iconName } = useMemo(() => useImageResolver(name, config?.imagesDir, size, mode), [name, config?.imagesDir, size, mode]);
  const { sizeStyle, sizeMap } = sizeStyles(size);

  const isCustomIcon = iconName && iconName in customIcons && !!customIcons[iconName];
  const _accessibilityLabel = accessibilityLabel ? accessibilityLabel : `Image of ${iconName}`;

  /**
   * Apply Colors
   */
  const colorRaw = cssColor(color);
  let colorTwClass = "";
  if (color && color in twColors) colorTwClass = twColors[color];

  const mask = useMemo(() => {
    const bgColor = !!colorTwClass ? {} : { backgroundColor: colorRaw };
    return {
      maskImage: `url(${src})`,
      WebkitMaskImage: `url(${src})`,
      maskSize: "100%",
      WebkitMaskSize: "100%",
      maskRepeat: "no-repeat",
      WebkitMaskRepeat: "no-repeat",
      maskPosition: "center",
      WebkitMaskPosition: "center",
      ...bgColor,
    };
  }, [name, src, colorRaw, colorTwClass]);

  const animationClasses = animationStyles(animate);
  const portraitAspectRatio = iconName && exceptionsList.includes(iconName);

  const computedSize = useMemo(() => {
    let width: string | number = sizeParse(sizeStyle.width);
    let height: string | number = sizeParse(sizeStyle.height);
    if (sizeMap.width) width = sizeMap.width;
    if (sizeMap.height) height = sizeMap.height ?? width;
    const heightWithAspectRatio = portraitAspectRatio ? height : isCustomIcon ? height : "auto";
    const widthWithAspectRatio = portraitAspectRatio ? "auto" : width;
    
    return {
      width: widthWithAspectRatio,
      height: heightWithAspectRatio,
    };
  }, [name, size]);

  let CustomIcon: FunctionComponent<CustomIconProps> | null = isCustomIcon ? customIcons[iconName] : null;

  const { iProps, imgProps } = useMemo(() => {
    const _altText = { "aria-label": _accessibilityLabel, alt: _accessibilityLabel };
    const _elementTiming = elementTiming ? {
      elementtiming: elementTiming,
    } : undefined;

    const iProps = {
      id,
      className:
        `cui__image cui-inline-block cui-box-border !cui-not-italic !cui-text-[0px] !cui-leading-[0px] ${animationClasses} ${
          canColor && !CustomIcon ? colorTwClass : ""
        } ${paddingClasses} ${marginClasses} ${className}`.trim(),
      style: canColor
        ? {
            ...style,
            ...computedSize,
            ...mask,
          }
        : {
            ...style,
            ...computedSize,
          },
      ..._altText,
      ...rest,
      ..._elementTiming,
      role: "img"
    };

    const ariaLabel = { alt: _accessibilityLabel, "aria-label": _accessibilityLabel };
    const loadingAttr = lazy ? { loading: "lazy" as "lazy" } : undefined;
    const _rest = canColor ? undefined : rest;

    const imgProps = {
      id,
      className:
        `${canColor ? "cui__image-hidden cui-invisible" : `cui__image cui-inline-block cui-box-border !cui-not-italic !cui-text-[0px] !cui-leading-[0px] ${animationClasses} ${paddingClasses} ${marginClasses} ${className}`}`.trim(),
      style: {
        ...style,
        ...computedSize,
        position: canColor ? "relative" : style?.position
      },
      src,
      srcSet: srcSet || undefined,
      ...ariaLabel,
      ...loadingAttr,
      defer,
      onError: (e) => handleError(e, setLoaded, iconName),
      ..._rest,
      ..._elementTiming    
    }
    canColor && !!elementTiming && delete imgProps["elementtiming"];

    return { iProps, imgProps };
  }, [
    id,
    className,
    animationClasses,
    canColor,
    name,
    mask,
    defer,
    computedSize,
    accessibilityLabel,
    style,
    colorTwClass,
    rest,
    paddingClasses,
    marginClasses,
    lazy,
    src,
    srcSet
  ]);

  if (!loaded || (!name && !folder)) return null;
  if (CustomIcon)
    return (
      <CustomIcon
        className={iProps.className}
        width={computedSize.width}
        height={computedSize.height}
        options={options}
        style={style}
        elementTiming={elementTiming}
      />
    );
  return canColor ? <i {...iProps}><img {...imgProps}/></i> : <img {...imgProps} />;
};

function handleError(e, dispatch, name) {
  console.error(
    `CUI: Error loading icon '${name}'.  Check spelling, and confirm src asset exists in expected location.`
  );
  dispatch(false);
}

function sizeParse(size) {
  if (!size || typeof size === "number") return size;
  if (typeof size === "string" && sizeUnitRE.test(size))
    return Number(size.replace(sizeUnitRE, ""));
  if (typeof size === "object")
    console.error("CUI Image [Icon]: `size` cannot be an object.");
}

Image.isImageType = true;
